import React, { useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { FacebookLogo, GoogleLogo, InstagramLogo, TiktokLogo, Lightning, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

const platforms = [
  { id: "facebook", label: "Facebook", Icon: FacebookLogo, color: "text-blue-600" },
  { id: "google", label: "Google Ads", Icon: GoogleLogo, color: "text-red-500" },
  { id: "instagram", label: "Instagram", Icon: InstagramLogo, color: "text-pink-500" },
  { id: "tiktok", label: "TikTok", Icon: TiktokLogo, color: "text-slate-900" },
];

export default function AdsPage({ embedded = false }) {
  const [service, setService] = useState("");
  const [goal, setGoal] = useState("leads");
  const [audience, setAudience] = useState("");
  const [offer, setOffer] = useState("");
  const [platform, setPlatform] = useState("facebook");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    if (!service) return toast.error("Enter a service");
    setLoading(true);
    try {
      const r = await api.post("/ads/generate", { platform, service, goal, audience, offer });
      setResult(r.data);
      toast.success(`${platform} ad copy generated`);
    } catch (e) { toast.error("Failed"); } finally { setLoading(false); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  return (
    <div data-testid="ads-page">
      {!embedded && (
        <PageHeader
          eyebrow="Paid Acquisition"
          title="High-converting ad copy for every platform."
          description="Pick a platform, fill the brief, get 3 ready-to-publish variants tailored to its character limits."
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="sharp-card p-6 lg:sticky lg:top-6 self-start">
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Platform</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {platforms.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`platform-${p.id}`}
                    onClick={() => setPlatform(p.id)}
                    className={`flex items-center gap-2 px-3 py-2.5 border rounded-sm text-sm transition-all ${
                      platform === p.id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 hover:border-slate-400"
                    }`}
                  >
                    <p.Icon size={18} weight="fill" />
                    <span>{p.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Service</Label>
              <Input data-testid="ad-service" placeholder="e.g. emergency plumbing" value={service} onChange={(e)=>setService(e.target.value)} className="rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Goal</Label>
              <Select value={goal} onValueChange={setGoal}>
                <SelectTrigger data-testid="ad-goal" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="leads">Generate leads</SelectItem>
                  <SelectItem value="calls">Get phone calls</SelectItem>
                  <SelectItem value="bookings">Online bookings</SelectItem>
                  <SelectItem value="awareness">Brand awareness</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Audience</Label>
              <Input data-testid="ad-audience" placeholder="e.g. homeowners 35-65" value={audience} onChange={(e)=>setAudience(e.target.value)} className="rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Offer</Label>
              <Input data-testid="ad-offer" placeholder="e.g. $50 off first job" value={offer} onChange={(e)=>setOffer(e.target.value)} className="rounded-sm mt-1" />
            </div>
            <Button data-testid="btn-generate-ad" onClick={generate} disabled={loading} className="w-full bg-navy hover:bg-navy-deep text-white font-semibold rounded-sm h-11">
              <Lightning className="mr-2" size={18} />{loading ? "Generating..." : "Generate 3 variants"}
            </Button>
          </div>
        </Card>

        <Card className="sharp-card p-6 lg:col-span-2 min-h-[400px]">
          {!result ? (
            <div className="text-slate-400 text-sm h-full flex items-center justify-center">Variants will appear here.</div>
          ) : result.data?.variants ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="eyebrow">{result.platform} · {result.data.variants.length} variants</div>
                <Badge className="rounded-sm bg-navy text-white">AI-optimised</Badge>
              </div>
              {result.data.variants.map((v, i) => (
                <div key={v.label || i} data-testid={`variant-${i}`} className="border border-slate-200 rounded-sm p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-display font-bold text-slate-900">{v.label}</div>
                    <Button variant="outline" size="sm" className="rounded-sm" onClick={()=>copy(JSON.stringify(v, null, 2))}><Copy size={12} className="mr-1" />Copy</Button>
                  </div>
                  {v.headline && <div className="mb-2"><div className="eyebrow !text-[10px] mb-0.5">Headline</div><div className="font-semibold text-slate-900">{v.headline}</div></div>}
                  {v.primary_text && <div className="mb-2"><div className="eyebrow !text-[10px] mb-0.5">Primary text</div><div className="text-sm text-slate-700">{v.primary_text}</div></div>}
                  {v.description && <div className="mb-2"><div className="eyebrow !text-[10px] mb-0.5">Description</div><div className="text-sm text-slate-700">{v.description}</div></div>}
                  {v.cta && <div className="mb-2"><div className="eyebrow !text-[10px] mb-0.5">CTA</div><div className="text-sm text-slate-700">{v.cta}</div></div>}
                  {v.hashtags?.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2">{v.hashtags.map((h) => <Badge key={h} variant="outline" className="rounded-sm text-xs">#{h.replace(/^#/, '')}</Badge>)}</div>}
                  {v.notes && <div className="text-xs text-slate-500 mt-2 italic">{v.notes}</div>}
                </div>
              ))}
            </div>
          ) : (
            <pre className="whitespace-pre-wrap text-sm text-slate-700">{result.raw || JSON.stringify(result, null, 2)}</pre>
          )}
        </Card>
      </div>
    </div>
  );
}
